import { useState } from 'react';
import { BrandMark } from '@cwos/ui-admin';
import { clearRememberedOrg, readRememberedOrg } from '../rememberedOrg';
import { post } from '../../lib/api.js';

/**
 * Sign in — restyled to the approved Phibian sign-in design (a Water brand
 * panel beside a Shore login panel). The OIDC BFF flow is UNCHANGED: the primary
 * action is a full-page navigation to `/auth/login/start` (nginx reverse-proxies
 * /auth/* to the API), then callback → `/auth/me`. The dev-login form is DEV-only
 * and statically dead-code-eliminated from production builds, mirroring the
 * backend's hard-404 on POST /auth/dev-login unless AUTH_TEST_MODE=true.
 *
 * Organization card: the sign-in screen renders pre-auth, so there is no server
 * org context (`me` is null). Rather than hardcode a tenant, the card shows the
 * last *remembered* org (persisted by the shell after successful auth) and is
 * omitted when none is known. The primary action is identical either way — the
 * org is resolved server-side after login (and multi-org switching stays in the
 * shell via /auth/switch-org).
 */
interface IdpProvider {
  id: string;
  name: string;
  loginUrl: string;
}

const IDENTITY_PROVIDERS: IdpProvider[] = [
  { id: 'google', name: 'Google', loginUrl: '/auth/login/start' },
  // Future providers drop in here without touching layout, e.g.:
  // { id: 'microsoft', name: 'Microsoft', loginUrl: '/auth/login/start?provider=microsoft' },
];

export function SignIn({ onDevLogin }: { onDevLogin: (subject: string) => Promise<void> }) {
  const [primary] = IDENTITY_PROVIDERS;
  const loginUrl = primary?.loginUrl ?? '/auth/login/start';
  const [remembered, setRemembered] = useState(readRememberedOrg);

  return (
    <main className="signin">
      <section className="signin-brand" aria-label="Phibian">
        <div className="signin-brand-inner">
          <div className="signin-logo">
            <BrandMark variant="onWater" size={39} />
            <span className="signin-wordmark">phibian</span>
          </div>

          <div className="signin-hero">
            <div className="signin-eyebrow">Operations without boundaries</div>
            <h1 className="signin-hero-h1">See the whole operation.</h1>
            <p className="signin-hero-p">
              One operating platform for the people, work, movement, systems, and intelligence that
              keep distributed organizations running.
            </p>
          </div>

          <div className="signin-brand-foot">
            <span>
              <i aria-hidden="true" /> Workforce
            </span>
            <span>
              <i aria-hidden="true" /> Field operations
            </span>
            <span>
              <i aria-hidden="true" /> Operational intelligence
            </span>
          </div>
        </div>
      </section>

      <section className="signin-panel">
        <div className="signin-wrap">
          <div className="signin-card">
            <h2 className="signin-h2">Welcome back</h2>
            <p className="signin-sub">Sign in to continue to your organization's workspace.</p>

            {remembered ? (
              <div className="signin-org">
                <div className="signin-org-avatar" aria-hidden="true">
                  {initials(remembered.name)}
                </div>
                <div className="signin-org-copy">
                  <div className="signin-org-label">Organization</div>
                  <div className="signin-org-name">{remembered.name}</div>
                </div>
                <button
                  type="button"
                  className="signin-change"
                  onClick={() => {
                    clearRememberedOrg();
                    setRemembered(null);
                  }}
                >
                  Change
                </button>
              </div>
            ) : null}

            <a className="signin-primary" href={loginUrl}>
              Continue to secure sign in
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                aria-hidden="true"
              >
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </a>

            <div className="invite-divider">
              <span>or sign in with your password</span>
            </div>

            <PasswordSignIn />

            <div className="signin-secure">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.9"
                aria-hidden="true"
              >
                <rect x="4" y="10" width="16" height="11" rx="2" />
                <path d="M8 10V7a4 4 0 0 1 8 0v3" />
              </svg>
              <span>
                Authentication is handled through your organization's secure identity provider.
              </span>
            </div>

            {import.meta.env.DEV ? <DevLoginForm onLogin={onDevLogin} /> : null}
          </div>

          <div className="signin-support">
            Need help? <a href="#">Contact support</a>
          </div>
        </div>

        <nav className="signin-legal" aria-label="Legal">
          <a href="#">Privacy</a>
          <a href="#">Security</a>
          <a href="#">Terms</a>
        </nav>
      </section>
    </main>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  const first = parts[0] as string;
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const last = parts[parts.length - 1] as string;
  return (first.charAt(0) + last.charAt(0)).toUpperCase();
}

function DevLoginForm({ onLogin }: { onLogin: (subject: string) => Promise<void> }) {
  const [subject, setSubject] = useState('dev-admin@generationhope.local');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onLogin(subject.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="signin-dev">
      <p className="rn-muted">Internal alpha — dev login (local/test only)</p>
      <form onSubmit={submit}>
        <input
          className="signin-input"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="external_auth_subject"
          autoComplete="off"
        />
        <button className="signin-devbtn" type="submit" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
      {error ? <p className="signin-err">{error}</p> : null}
    </div>
  );
}

/**
 * Email + password sign-in, with the second-factor step it may lead to.
 *
 * Added because invitation acceptance can now create a password credential, and
 * until this existed that credential was a one-way door: someone could set a
 * password, get in once from the invitation link, and have no way back — the
 * only sign-in path was the federated one.
 *
 * The MFA step is not a separate screen. Identity Platform tells us "the
 * password was right and that is not enough" in the same round trip, and the
 * BFF holds the half-finished sign-in in an HttpOnly cookie, so the browser
 * never handles the pending credential. All this component does is swap which
 * field it is asking for.
 */
function PasswordSignIn() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  /**
   * Continue with Google can land here already owing a second factor.
   *
   * That sign-in is a full-page navigation, so the callback cannot answer it
   * with the JSON the password path uses — it holds the pending credential in
   * the same HttpOnly cookie and sends the browser back with ?mfa=required.
   * The step is identical from here on: the same field, posting to the same
   * endpoint, against the same cookie. Only the way we arrived differs.
   */
  const arrivedOwingSecondFactor =
    new URLSearchParams(window.location.search).get('mfa') === 'required';

  const [phase, setPhase] = useState<'credentials' | 'mfa' | 'forgot'>(
    arrivedOwingSecondFactor ? 'mfa' : 'credentials',
  );
  // The factor's display name is not carried across the redirect: it would mean
  // putting an account detail in a URL to save one generic word.
  const [factors, setFactors] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** A completed sign-in reloads rather than transitioning: the app must boot
   *  from a fresh /auth/me against the new session cookie. */
  function enter(result: { chooseOrganization?: boolean }): void {
    window.location.assign(result.chooseOrganization ? '/select-org' : '/');
  }

  async function submitCredentials(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await post<{
        mfaRequired?: boolean;
        factors?: string[];
        chooseOrganization?: boolean;
      }>('/auth/password/login', { email: email.trim(), password });
      if (result.mfaRequired) {
        setFactors(result.factors ?? []);
        setPhase('mfa');
        setBusy(false);
        return;
      }
      enter(result);
    } catch (err) {
      setError((err as Error).message || 'We could not sign you in.');
      setBusy(false);
    }
  }

  async function submitCode(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      enter(
        await post<{ chooseOrganization?: boolean }>('/auth/password/mfa', { code: code.trim() }),
      );
    } catch (err) {
      setError((err as Error).message || 'That code was not accepted.');
      setBusy(false);
    }
  }

  if (phase === 'forgot') {
    return <ForgotPassword onBack={() => setPhase('credentials')} />;
  }

  if (phase === 'mfa') {
    return (
      <form onSubmit={submitCode}>
        <p className="signin-sub" style={{ marginTop: 0 }}>
          Enter the 6-digit code from {factors[0] ?? 'your authenticator app'}.
        </p>
        <label className="invite-field">
          <span className="invite-label">Verification code</span>
          <input
            className="signin-input"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
            autoFocus
          />
        </label>
        {error ? (
          <p className="signin-err" role="alert">
            {error}
          </p>
        ) : null}
        <button className="signin-primary" type="submit" disabled={busy}>
          {busy ? 'Checking…' : 'Verify'}
        </button>
        <button
          type="button"
          className="signin-change"
          style={{ marginTop: 12 }}
          onClick={() => {
            setPhase('credentials');
            setCode('');
            setError(null);
          }}
        >
          Use a different account
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={submitCredentials}>
      <label className="invite-field">
        <span className="invite-label">Email</span>
        <input
          className="signin-input"
          type="email"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </label>
      <label className="invite-field">
        <span className="invite-label">Password</span>
        <input
          className="signin-input"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
      </label>
      {error ? (
        <p className="signin-err" role="alert">
          {error}
        </p>
      ) : null}
      <button className="signin-primary" type="submit" disabled={busy}>
        {busy ? 'Signing in…' : 'Sign in'}
      </button>
      <button
        type="button"
        className="signin-change"
        style={{ marginTop: 12 }}
        onClick={() => setPhase('forgot')}
      >
        Forgot your password?
      </button>
    </form>
  );
}

/**
 * Reset request.
 *
 * The confirmation is deliberately the same whether or not the address has an
 * account, because the endpoint answers 202 either way — telling an anonymous
 * visitor which addresses exist is the cheapest account-enumeration there is,
 * needing no password to try. The copy is written to be true in both cases
 * rather than implying a message is definitely on its way.
 */
function ForgotPassword({ onBack }: { readonly onBack: () => void }) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await post('/auth/password/reset', { email: email.trim() });
    } catch {
      // The server answers 202 for everything; a transport failure here should
      // not hand the visitor a different outcome than a success would.
    }
    setSent(true);
    setBusy(false);
  }

  if (sent) {
    return (
      <>
        <p className="signin-sub" style={{ marginTop: 0 }}>
          If there&rsquo;s a Phibian account for <strong>{email}</strong>, a reset link is on its
          way. It expires shortly, so use it soon.
        </p>
        <button type="button" className="signin-change" onClick={onBack}>
          Back to sign in
        </button>
      </>
    );
  }

  return (
    <form onSubmit={submit}>
      <p className="signin-sub" style={{ marginTop: 0 }}>
        Enter your email and we&rsquo;ll send you a link to set a new password.
      </p>
      <label className="invite-field">
        <span className="invite-label">Email</span>
        <input
          className="signin-input"
          type="email"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoFocus
        />
      </label>
      <button className="signin-primary" type="submit" disabled={busy}>
        {busy ? 'Sending…' : 'Send reset link'}
      </button>
      <button type="button" className="signin-change" style={{ marginTop: 12 }} onClick={onBack}>
        Back to sign in
      </button>
    </form>
  );
}
